import { Job } from "@/data/jobs";
import { getJobFacts } from "@/utils/jobGeo";

/**
 * Generate JobPosting structured data for a single job
 * Compliant with Google Jobs requirements (schema.org/JobPosting)
 * 
 * Required fields: title, description, datePosted, hiringOrganization, jobLocation
 * Recommended fields: baseSalary, employmentType, validThrough, identifier, directApply
 */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const list = (heading: string, items?: string[]): string => {
  if (!items || items.length === 0) return "";
  return `<h3>${escapeHtml(heading)}</h3><ul>${items
    .map((i) => `<li>${escapeHtml(i)}</li>`)
    .join("")}</ul>`;
};

/**
 * Build the full advert as HTML, which is what Google Jobs expects in
 * `description` (summary only is a common reason for exclusion).
 */
export const buildJobDescriptionHtml = (job: Job): string => {
  const facts: string[] = [];
  if (job.payRate) facts.push(`Pay: ${job.payRate}`);
  if (job.schedule) facts.push(`Hours: ${job.schedule}`);
  if (job.contractType) facts.push(`Contract: ${job.contractType}`);
  if (job.duration) facts.push(`Duration: ${job.duration}`);
  if (job.dbsRequired) facts.push(`DBS: ${job.dbsRequired}`);
  if (job.location) facts.push(`Location: ${job.location}`);

  return [
    `<p>${escapeHtml(job.description)}</p>`,
    list("Role details", facts),
    list("Key responsibilities", job.responsibilities),
    list("Requirements", job.qualifications),
    list("Skills and competencies", job.competencies),
    list("Uniform and equipment", job.uniform),
    list("Health and safety", job.healthSafety),
  ]
    .filter(Boolean)
    .join("");
};

export const isJobPostingExpired = (job: Job): boolean => {
  if (!job.closingDate) return false;
  const closing = Date.parse(job.closingDate);
  if (Number.isNaN(closing)) return false;
  // Roles advertised as ongoing stay open past their original closing date
  if (job.duration && job.duration.toLowerCase().includes("ongoing")) return false;
  return closing < Date.now();
};

export const generateJobPostingSchema = (job: Job) => {
  const facts = getJobFacts(job);
  // Prefer the real posting date; only fall back when it is missing.
  // A datePosted in the future is invalid, so the fallback is always capped.
  const getDatePosted = (): string => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    if (job.datePosted) return job.datePosted;
    if (job.closingDate) {
      const postedDate = new Date(job.closingDate);
      postedDate.setDate(postedDate.getDate() - 30);
      const capped = postedDate.getTime() > weekAgo.getTime() ? weekAgo : postedDate;
      return capped.toISOString().split("T")[0];
    }
    return weekAgo.toISOString().split("T")[0];
  };


  /**
   * Only emit validThrough when it is still in the future. A past date makes
   * Google treat the posting as expired and drop it from the jobs experience.
   */
  const getValidThrough = (): string | null => {
    if (job.closingDate) {
      const closing = new Date(job.closingDate);
      if (!Number.isNaN(closing.getTime()) && closing.getTime() > Date.now()) {
        return closing.toISOString();
      }
      if (isJobPostingExpired(job)) return null;
    }
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 60);
    return futureDate.toISOString();
  };

  // Map contract type to Google's employmentType values
  const getEmploymentType = (): string | string[] => {
    const baseType = job.contractType === "Permanent" ? "FULL_TIME" : 
                     job.contractType === "Temporary" ? "TEMPORARY" : "CONTRACTOR";
    
    // Check schedule for part-time indication
    if (job.schedule?.toLowerCase().includes('part-time') || 
        job.schedule?.toLowerCase().includes('part time')) {
      return job.contractType === "Permanent" ? "PART_TIME" : [baseType, "PART_TIME"];
    }
    return baseType;
  };

  // Parse salary for min/max values (handles ranges like "£25,000 - £30,000")
  const getSalaryData = () => {
    const salaryInfo = extractSalaryInfo(job.payRate);
    if (!salaryInfo) return undefined;
    
    if (salaryInfo.minValue && salaryInfo.maxValue && salaryInfo.minValue !== salaryInfo.maxValue) {
      return {
        "@type": "MonetaryAmount",
        "currency": "GBP",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": salaryInfo.minValue,
          "maxValue": salaryInfo.maxValue,
          "unitText": salaryInfo.unitText
        }
      };
    }
    
    return {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "value": {
        "@type": "QuantitativeValue",
        "value": salaryInfo.minValue || 0,
        "unitText": salaryInfo.unitText
      }
    };
  };

  // Split "Street, Town, County, SP1" into address parts, ignoring any
  // postcode fragment (that is carried separately in postalCode)
  const locationParts = job.location
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !/^[A-Z]{1,2}\d{1,2}[A-Z]?(\s+\d[A-Z]{2})?$/i.test(p));
  const validThrough = getValidThrough();

  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": buildJobDescriptionHtml(job),
    "url": `https://pamprecruitment.co.uk/jobs/${job.id}`,
    "datePosted": getDatePosted(),
    ...(validThrough && { "validThrough": validThrough }),
    "employmentType": getEmploymentType(),
    "hiringOrganization": {
      "@type": "Organization",
      "@id": "https://pamprecruitment.co.uk/#organization",
      "name": "PAMP Recruitment Agency LTD",
      "sameAs": "https://pamprecruitment.co.uk",
      "logo": "https://pamprecruitment.co.uk/pamp-logo.png"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        ...(locationParts.length > 2 && { "streetAddress": locationParts[0] }),
        "addressLocality": locationParts.length > 2 ? locationParts[1] : locationParts[0],
        "addressRegion": (locationParts.length > 2 ? locationParts[2] : locationParts[1]) || "United Kingdom",
        "addressCountry": "GB",
        ...(job.postcodes && job.postcodes.length > 0 && {
          "postalCode": job.postcodes[0]
        })
      }
    },
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    ...(getSalaryData() && { "baseSalary": getSalaryData() }),
    "industry": job.category,
    "occupationalCategory": job.category,
    "directApply": true,
    ...(job.openings && { "totalJobOpenings": job.openings }),
    ...(job.responsibilities && {
      "responsibilities": job.responsibilities.join("; ")
    }),
    ...(job.qualifications && {
      "qualifications": job.qualifications.join("; ")
    }),
    ...(job.schedule && {
      "workHours": job.schedule
    }),
    "identifier": {
      "@type": "PropertyValue",
      "name": "PAMP Recruitment",
      "value": job.id
    },
    "mainEntityOfPage": `https://pamprecruitment.co.uk/jobs/${job.id}`,
    "subjectOf": {
      "@type": "WebPage",
      "@id": `https://pamprecruitment.co.uk/jobs/${job.id}`,
      "name": `${job.title} in ${facts.town}`
    }
  };
};


/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

/**
 * Generate FAQPage structured data
 */
export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate ItemList structured data for job listings.
 * Google's guidance is that full JobPosting markup belongs on the individual
 * job page only, so list pages link out rather than duplicating each advert.
 */
export const generateJobListSchema = (jobs: Job[]) => {
  return [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "numberOfItems": jobs.length,
      "itemListElement": jobs.slice(0, 50).map((job, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://pamprecruitment.co.uk/jobs/${job.id}`,
        "name": job.title
      }))
    }
  ];
};

/**
 * WebSite + SearchAction markup so Google can surface a site search box
 */
export const generateWebsiteSearchSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "PAMP Recruitment",
  "url": "https://pamprecruitment.co.uk",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://pamprecruitment.co.uk/jobs/search?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
});


/**
 * Generate CollectionPage structured data for regional job pages
 */
export const generateRegionalJobSchema = (regionName: string, regionSlug: string, jobs: Job[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${regionName} Jobs | PAMP Recruitment`,
    "description": `Browse ${jobs.length} job vacancies in ${regionName}. Find cleaning, hospitality, retail and admin jobs with PAMP Recruitment.`,
    "url": `https://pamprecruitment.co.uk/jobs/${regionSlug}`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": jobs.length,
      "itemListElement": jobs.slice(0, 30).map((job, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://pamprecruitment.co.uk/jobs/${job.id}`,
        "name": job.title
      }))
    }
  };
};

/**
 * Helper function to extract salary information including ranges
 * Handles formats like: "£12.71 per hour", "£25,000 - £30,000", "£6,000 (Fixed Fee)"
 */
function extractSalaryInfo(payRate: string): { minValue: number; maxValue?: number; unitText: string } | undefined {
  const lowerPayRate = payRate.toLowerCase();
  
  // Determine unit
  let unitText = "YEAR";
  if (lowerPayRate.includes("/hour") || lowerPayRate.includes("per hour")) {
    unitText = "HOUR";
  } else if (lowerPayRate.includes("/day") || lowerPayRate.includes("per day")) {
    unitText = "DAY";
  } else if (lowerPayRate.includes("/week") || lowerPayRate.includes("per week")) {
    unitText = "WEEK";
  } else if (lowerPayRate.includes("/month") || lowerPayRate.includes("per month")) {
    unitText = "MONTH";
  }
  
  // Extract all numeric values
  const matches = payRate.match(/£([\d,.]+)/g);
  
  if (!matches || matches.length === 0) {
    return undefined;
  }
  
  const values = matches.map(m => parseFloat(m.replace(/[£,]/g, '')));
  
  if (values.length >= 2) {
    return {
      minValue: Math.min(...values),
      maxValue: Math.max(...values),
      unitText
    };
  }
  
  return { minValue: values[0], unitText };
}
